import type { AgentApiJobSourcesShow } from '@/types/serializers';
import clsx from 'clsx';
import { MdAutorenew, MdEdit, MdError } from 'react-icons/md';
import { Button } from '@/components/Button';
import { formatRelativeDate } from '@/utils/formatRelativeDate';
import LinkedAccountsStatusChip from '../LinkedAccountsStatusChip';

type Props = {
  jobSource: AgentApiJobSourcesShow;
  onEditTitle: () => void;
  onDesync: () => void;
  onReload: () => void;
  isReloading: boolean;
};

function buildErrorMessage(jobSource: AgentApiJobSourcesShow) {
  if (jobSource.login_status === 'failed') return 'ログインに失敗しました';
  if (jobSource.collection_status === 'failed') return 'セットアップに失敗しました';
  if (jobSource.import_status === 'failed') return '求人の取り込みに失敗しました';
  if (jobSource.status === 'error') return '同期に失敗しました';
  return null;
}

export default function LinkedAccountDetailHeader({
  jobSource,
  onEditTitle,
  onDesync,
  onReload,
  isReloading,
}: Props) {
  const metaItems = [
    { label: '連携元', value: jobSource.source_display_type || '' },
    { label: '作成者', value: jobSource.organization_user?.name || '-' },
    ...(jobSource.service_type === 'normal'
      ? [{ label: '求人数', value: String(jobSource.count_of_job_posts || 0) }]
      : []),
  ];
  const errorMessage = buildErrorMessage(jobSource);

  return (
    <div className="flex flex-col gap-sm rounded-sm bg-white p-lg shadow-layer1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-md">
          <LinkedAccountsStatusChip
            status={jobSource.status}
            label={jobSource.display_status_name}
          />
          <div className="flex items-center gap-xs">
            <span className="text-heading-3xs">最終同期</span>
            <span className="text-body-sm">
              {jobSource.last_crawled_at ? formatRelativeDate(jobSource.last_crawled_at) : '未同期'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-sm">
          <Button type="button" color="danger-secondary" onClick={onDesync}>
            <img
              src="/agent/linked_accounts/icon-conversion-path-off.svg"
              alt=""
              width={20}
              height={20}
            />
            同期解除
          </Button>
          <Button
            type="button"
            onClick={onReload}
            disabled={!jobSource.is_able_to_reload || isReloading}
          >
            <MdAutorenew size={20} />
            {isReloading ? '同期中...' : '再同期'}
          </Button>
        </div>
      </div>
      {errorMessage && (
        <div className="flex items-center gap-xs rounded-sm border border-solid border-danger bg-danger_lighten px-md py-sm">
          <MdError size={20} className="shrink-0 text-danger" />
          <span className="text-body-sm">{errorMessage}</span>
        </div>
      )}
      <div className="flex items-center gap-xs">
        <h1 className="text-heading-md">{jobSource.title}</h1>
        {jobSource.is_able_to_edit && (
          <Button
            type="button"
            color="tertiary"
            iconOnly
            aria-label="タイトルを編集"
            onClick={onEditTitle}
          >
            <MdEdit size={20} />
          </Button>
        )}
      </div>
      <div className="flex items-center gap-md">
        {metaItems.map((item, index) => (
          <div
            key={item.label}
            className={clsx(
              'flex items-center gap-xs pr-md',
              index < metaItems.length - 1 && 'border-r border-solid border-border'
            )}
          >
            <span className="text-heading-3xs">{item.label}</span>
            <span className="text-body-sm">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
